import { MAPS, getMapWithLayout, type MapDef } from './map';
import { MISSION_WAVES, type WaveDef } from './waves';
import { TEST_UNLOCK_ALL } from '../config/constants';

export interface MissionDef {
  id: string;
  index: number;
  name: string;
  mapId: string;
  /** Mission that must be completed first; null = always available. */
  requires: string | null;
  unlocksNote: string;
  accent: string;
}

export const MISSIONS: MissionDef[] = [
  {
    id: 'kappa',
    index: 1,
    name: 'OUTPOST KAPPA',
    mapId: 'kappa',
    requires: null,
    unlocksNote: 'Tutorial hold — learn towers and abilities.',
    accent: '#d4a24a',
  },
  {
    id: 'helios',
    index: 2,
    name: 'HELIOS DRIFT',
    mapId: 'helios',
    requires: 'kappa',
    unlocksNote: 'Shields and runners on ice. Energy weapons shine.',
    accent: '#6ec6ff',
  },
  {
    id: 'cinder',
    index: 3,
    name: 'CINDER REACH',
    mapId: 'cinder',
    requires: 'helios',
    unlocksNote: 'Tight pads, thermal-vulnerable swarms, fewer turrets.',
    accent: '#ff7a3c',
  },
  {
    id: 'meridian',
    index: 4,
    name: 'MERIDIAN SPIRE',
    mapId: 'meridian',
    requires: 'cinder',
    unlocksNote: 'Long approach. Jam fields and ranged pressure.',
    accent: '#b06cff',
  },
  {
    id: 'ashfall',
    index: 5,
    name: 'ASHFALL TERMINAL',
    mapId: 'ashfall',
    requires: 'meridian',
    unlocksNote: 'Elite-heavy urban siege. Nightmare pressure.',
    accent: '#ff5a5a',
  },
  {
    id: 'theta',
    index: 6,
    name: 'VAULT THETA',
    mapId: 'theta',
    requires: 'ashfall',
    unlocksNote: 'Short lane, scarce pads, Colossus finale.',
    accent: '#7dce5a',
  },
  {
    id: 'obsidian',
    index: 7,
    name: 'OBSIDIAN FOUNDRY',
    mapId: 'obsidian',
    requires: 'theta',
    unlocksNote: 'Three volcanic lanes merge at the foundry gate. Expect armoured surges.',
    accent: '#ff7a3c',
  },
  {
    id: 'cryostation',
    index: 8,
    name: 'CRYOSTATION NADIR',
    mapId: 'cryostation',
    requires: 'obsidian',
    unlocksNote: 'Ice-shelf lanes and shielded runners converge under storm cover.',
    accent: '#8ec8ff',
  },
  {
    id: 'floodway',
    index: 9,
    name: 'MERIDIAN FLOODWAY',
    mapId: 'floodway',
    requires: 'cryostation',
    unlocksNote: 'Wet logistics platforms create three long firing windows.',
    accent: '#b06cff',
  },
  {
    id: 'verdigris',
    index: 10,
    name: 'VERDIGRIS BLOOM',
    mapId: 'verdigris',
    requires: 'floodway',
    unlocksNote: 'Alien growth and bridge lanes favour thermal and explosive counters.',
    accent: '#7dce5a',
  },
  {
    id: 'umbra',
    index: 11,
    name: 'UMBRA LIFT',
    mapId: 'umbra',
    requires: 'verdigris',
    unlocksNote: 'Storm-basin lanes join at the elevator approach for the final hold.',
    accent: '#9ad4ff',
  },
  {
    id: 'aurora',
    index: 12,
    name: 'AURORA CITADEL',
    mapId: 'aurora',
    requires: 'umbra',
    unlocksNote: 'Two ice shelves and a reactor bridge. High ground controls the merge.',
    accent: '#75e6ff',
  },
  {
    id: 'emberfall',
    index: 13,
    name: 'EMBER VAULT',
    mapId: 'emberfall',
    requires: 'aurora',
    unlocksNote: 'One switchback across three elevations. Blind corners hide the heaviest push.',
    accent: '#ff7b32',
  },
  {
    id: 'tideline',
    index: 14,
    name: 'TIDELINE ARRAY',
    mapId: 'tideline',
    requires: 'emberfall',
    unlocksNote: 'Three storm causeways converge at a contested communications platform.',
    accent: '#5cd9d1',
  },
];

export function getMission(id: string): MissionDef {
  return MISSIONS.find((m) => m.id === id) ?? MISSIONS[0];
}

export function getMissionMap(id: string): MapDef {
  const m = getMission(id);
  return getMapWithLayout(m.mapId);
}

export function getMissionWaves(id: string): WaveDef[] {
  const authored = MISSION_WAVES[id] ?? MISSION_WAVES.kappa;
  // The opening trilogy is a shorter onboarding arc; the full campaign
  // unlocks the 30-wave endurance format from Meridian onward.
  const target = id === 'kappa' || id === 'helios' || id === 'cinder' ? 20 : 30;
  return authored.slice(0, target).map((wave, index) => ({ ...wave, id: index + 1 }));
}

export function isMissionUnlocked(id: string, completed: string[], devMode = false): boolean {
  if (TEST_UNLOCK_ALL || devMode) return true;
  const m = getMission(id);
  if (!m.requires) return true;
  return completed.includes(m.requires);
}

/**
 * Returns the first unbeaten mission in campaign order that is currently
 * available to the commander. Keeping this in the mission data module means
 * the results screen and operations board cannot drift into different routing
 * decisions after a clear.
 */
export function getNextUnbeatenMissionId(completed: string[], devMode = false): string | undefined {
  return MISSIONS.find((mission) => (
    !completed.includes(mission.id)
    && isMissionUnlocked(mission.id, completed, devMode)
  ))?.id;
}
